import React, { FC, useContext, useMemo } from "react";
import { defineMessages, useIntl } from "react-intl";
import { FACTORS_TO_SHOW } from "@app/service/user";
import { Button } from "@natera/form";
import {
  ConfigContext,
  ErrorContext,
  IntlContext,
  MfaErrorContext,
  MfaSetupContext,
  NotificationContext,
  ProfileContext,
} from "@app/provider";
import { CodeInput, SpinnerView } from "@app/components";
import { messages as verifyCodeFormMessages } from "@app/components/verifyCodeForm/verifyCodeForm";
import { convertPhoneNumberToE164 } from "@app/utils/convertPhoneNumber";
import { useVerifyCodeForm } from "@app/hooks";

import "./verifyCodeFormStepper.scss";
import { verifyCodeLength } from "@app/utils/verifyCodeLength";

const stepperMessages = defineMessages({
  verifyCodeFormStepperSentToTarget: {
    id: "verifyCodeFormStepperSentToTarget",
    defaultMessage: "Enter your code sent to {target}",
  },
  verifyCodeFormStepperArrivalSubtitle: {
    id: "verifyCodeFormStepperArrivalSubtitle",
    defaultMessage: "It may take a few minutes to arrive.",
  },
  verifyCodeFormStepperBackButton: {
    id: "verifyCodeFormStepperBackButton",
    defaultMessage: "Back",
  },
});

const messages = { ...verifyCodeFormMessages, ...stepperMessages };

interface VerifyCodeFormStepperProps extends React.HTMLAttributes<HTMLDivElement> {
  type: FACTORS_TO_SHOW;
  profile?: {
    email?: string;
    phoneNumber?: string;
  };
  handleCode: (code: string) => void;
  handleResend: () => void;
  goBack?: () => void;
}

const VerifyCodeFormStepper: FC<VerifyCodeFormStepperProps> = ({
  type,
  profile,
  handleCode,
  handleResend,
  goBack,
}) => {
  const intl = useIntl();
  const { config } = useContext(ConfigContext);
  const { notifications } = useContext(NotificationContext);
  const { isLoading: profileIsLoading } = useContext(ProfileContext);

  const { retranslateErrors } = useContext(MfaErrorContext);

  const { isLoading: mfaIsLoading } = useContext(MfaSetupContext);

  const { profileDataIsLoading, updatePatientIsLoading } =
    useContext(ProfileContext);

  const errorController = useContext(ErrorContext);
  const { getValidationError, clearValidationError } = errorController;
  const { currentLanguage } = useContext(IntlContext);

  React.useEffect(() => {
    retranslateErrors(errorController);
  }, [currentLanguage]);

  const {
    timeLeft,
    phoneNumber,
    updateMobileNumberDialog,
    handleResendCodeClick,
    handleUpdateMobileNumberClick,
  } = useVerifyCodeForm({
    defaultCounterTime: config.DEFAULT_COUNTER_TIME,
    defaultTimeLeft: config.DEFAULT_TIME_LEFT,
    validationErrorKey: "codeStepper",
    profile,
    pageName: "setupProfile",
  });

  const handleCodeSubmit = (code: string) => {
    handleCode(code);
  };

  const handleCodeChange = () => {
    clearValidationError("codeStepper");
  };

  const onResendCodeClick = () => {
    handleResendCodeClick(handleResend);
  };

  const goBackClicked = () => {
    clearValidationError("codeStepper");
    goBack?.();
  };

  const displayCodeVerificationTarget = () => {
    return type === FACTORS_TO_SHOW.email
      ? profile?.email
      : convertPhoneNumberToE164(phoneNumber || profile?.phoneNumber || "");
  };

  const displayUpdatePhoneButton =
    type === FACTORS_TO_SHOW.sms || type === FACTORS_TO_SHOW.call;

  const isLoading = useMemo(
    () =>
      profileIsLoading ||
      mfaIsLoading ||
      profileDataIsLoading ||
      updatePatientIsLoading,
    [
      profileIsLoading,
      mfaIsLoading,
      profileDataIsLoading,
      updatePatientIsLoading,
    ]
  );

  return (
    <div className="verify-code-stepper-container">
      {notifications}
      <div className="verify-code-content">
        <h3>
          {intl.formatMessage(messages.verifyCodeFormStepperSentToTarget, {
            target: displayCodeVerificationTarget(),
          })}
        </h3>

        <div className="verify-code-to">
          <span>
            {intl.formatMessage(messages.verifyCodeFormStepperArrivalSubtitle)}
          </span>
        </div>

        <CodeInput
          onCodeComplete={handleCodeSubmit}
          onCodeChange={handleCodeChange}
          hasError={!!getValidationError("codeStepper")}
          errorMessage={getValidationError("codeStepper")}
          codeFieldLength={verifyCodeLength(type)}
        />

        <div className="verify-code-buttons-container">
          {timeLeft === 0 ? (
            <>
              <Button unelevated type="button" onClick={onResendCodeClick}>
                {intl.formatMessage(messages.verifyCodeFormResendButton)}
              </Button>
            </>
          ) : (
            <Button unelevated type="button" disabled>
              {intl.formatMessage(messages.verifyCodeFormResendIn, {
                timeLeft,
              })}
            </Button>
          )}

          {displayUpdatePhoneButton && (
            <Button
              className="update-mobile-number-button"
              outlined
              type="button"
              onClick={handleUpdateMobileNumberClick}
            >
              {intl.formatMessage(messages.verifyCodeFormUpdateMobileNumber)}
            </Button>
          )}
          {goBack && (
            <Button
              className="go-back-button"
              type="button"
              onClick={goBackClicked}
            >
              {intl.formatMessage(messages.verifyCodeFormStepperBackButton)}
            </Button>
          )}
        </div>
      </div>
      {updateMobileNumberDialog.getDialog()}
      <SpinnerView isLoading={isLoading} />
    </div>
  );
};

export default VerifyCodeFormStepper;
